import { inject } from '@angular/core';
import { ActionOverRecordTypes, FieldDefinition, FieldBuilder, IActionsSchema, MenuCategories, MenuSubcategories, MENU_CATEGORY_KEY, MENU_SUBCATEGORY_KEY, MENU_NAME_SUFFIX, PERIOD_TYPE_NAME_PLANNED_PLANNING_SEASON, triggerAction, ZendraLocalizationServices } from '@zendra/shared';
import { PeriodService, RegionService, VendorService } from 'cbd-proxies';
import { PipelinePropertyPlanningOrchestratorService } from 'orchestrator';
import { PipelinePropertyService } from '../proxy/operational/controllers';
import { PipelinePlannedPlantation } from '../pipeline-planned-plantation/pipeline-planned-plantation';

FieldBuilder.setNamespace('Operational');

const namespace = ZendraLocalizationServices.Operational;

const isPositiveInteger = (value: number): boolean => Number.isInteger(value) && value > 0;

const parseEntityId = (value: string): number | null => {
  const parsedValue = Number(value);

  return isPositiveInteger(parsedValue) ? parsedValue : null;
};

const extractEntityIds = (rawValue: unknown): number[] => {
  if (typeof rawValue === 'number') {
    return isPositiveInteger(rawValue) ? [rawValue] : [];
  }

  if (typeof rawValue !== 'string') {
    return [];
  }

  return rawValue
    .split(',')
    .map(part => parseEntityId(part.trim()))
    .filter((parsedValue): parsedValue is number => parsedValue !== null);
};

const normalizeEntityIds = (value: unknown): number[] => {
  if (value === null || value === undefined || value === '') {
    return [];
  }

  const rawValues = Array.isArray(value) ? value : [value];

  return [...new Set(rawValues.flatMap(extractEntityIds))];
};

const loadPipelinePlannedPlantationHeaderVendors = (service: VendorService, query: any, config?: any) => {
  const {
    fantasyName,
    ...rest
  } = query ?? {};

  return service.getList(
    {
      ...rest,
      legalName: query?.legalName ?? fantasyName,
    },
    config,
  );
};

const loadPipelinePlannedPlantationHeaderProperties = (
  query: any,
  config?: any,
) => {
  const regionIds = normalizeEntityIds(query?.regionIds);

  const service = inject(PipelinePropertyPlanningOrchestratorService);

  return service.getList(
    {
      name: query?.name,
      regionId: regionIds,
      sorting: query?.sorting ?? 'name',
      skipCount: query?.skipCount,
      maxResultCount: query?.maxResultCount,
    },
    config,
  );
};

export const PIPELINEPLANNEDPLANTATIONHEADER_ACTION_IDS = {
  removeCascade: 0,
};

export const PIPELINEPLANNEDPLANTATIONHEADER_ACTIONS_CONFIG: IActionsSchema = {
  singleRecord: [
    triggerAction(
      PIPELINEPLANNEDPLANTATIONHEADER_ACTION_IDS.removeCascade,
      'Action.Trigger.RemoveCascadeFromPipelinePlan',
      ActionOverRecordTypes.SingleRecord,
      namespace,
    ),
  ],
};

export const PIPELINEPLANNEDPLANTATIONHEADER_FIELDS: FieldDefinition[] = [
  FieldBuilder
    .number(
      'pipelinePlannedPlantationHeaderId',
      'Entity.PipelinePlannedPlantationHeader.PipelinePlannedPlantationHeaderId',
    )
    .hideByDefault()
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'pipelinePropertyId',
    'Entity.PipelinePlannedPlantationHeader.PipelinePropertyName',
    PipelinePropertyService
  )
    .required()
    .display('name')
    .displayFromNested('pipelinePropertyName')
    .sortBy('pipelinePropertyName')
    .keyField('id')
    .loadWith((_service, query, config) => loadPipelinePlannedPlantationHeaderProperties(query, config))
    .prefilter({ multiple: true, order: 4, dependsOn: { regionPrefilterId: 'regionIds' } })
    .build(),
  FieldBuilder.foreignKey(
    'periodPrefilterId',
    'Entity.PipelinePlannedPlantationHeader.PeriodName',
    PeriodService,
  )
    .display('name')
    .keyField('id')
    .queryFilter({ periodTypeName: PERIOD_TYPE_NAME_PLANNED_PLANNING_SEASON })
    .hideByDefault()
    .readOnly()
    .sortable(false)
    .filterable(false)
    .prefilter({ multiple: true, order: 2, queryKey: 'periodId' })
    .build(),
  FieldBuilder
    .number('periodId', 'Entity.PipelinePlannedPlantationHeader.PeriodId')
    .hideByDefault()
    .readOnly()
    .build(),
  FieldBuilder
    .text('periodName', 'Entity.PipelinePlannedPlantationHeader.PeriodName')
    .readOnly()
    .build(),
  FieldBuilder
    .number('signatureReasonId', 'Entity.PipelinePlannedPlantationHeader.SignatureReasonId')
    .hideByDefault()
    .readOnly()
    .build(),
  FieldBuilder
    .text('signatureReasonName', 'Entity.PipelinePlannedPlantationHeader.SignatureReasonName')
    .readOnly()
    .build(),
  FieldBuilder
    .number('dinamaReasonId', 'Entity.PipelinePlannedPlantationHeader.DinamaReasonId')
    .hideByDefault()
    .readOnly()
    .build(),
  FieldBuilder
    .text('dinamaReasonName', 'Entity.PipelinePlannedPlantationHeader.DinamaReasonName')
    .readOnly()
    .build(),
  FieldBuilder
    .number('zoneId', 'Entity.PipelinePlannedPlantationHeader.ZoneId')
    .hideByDefault()
    .readOnly()
    .build(),
  FieldBuilder
    .text('zoneName', 'Entity.PipelinePlannedPlantationHeader.ZoneName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'regionPrefilterId',
    'Entity.Region',
    RegionService,
  )
    .display('name')
    .keyField('id')
    .hideByDefault()
    .readOnly()
    .sortable(false)
    .filterable(false)
    .prefilter({ multiple: true, order: 3, queryKey: 'regionId' })
    .build(),
  FieldBuilder
    .number('regionId', 'Entity.PipelinePlannedPlantationHeader.RegionId')
    .hideByDefault()
    .readOnly()
    .build(),
  FieldBuilder
    .text('regionCode', 'Entity.PipelinePlannedPlantationHeader.RegionCode')
    .readOnly()
    .build(),
  FieldBuilder
    .text('regionName', 'Entity.PipelinePlannedPlantationHeader.RegionName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'vendorId',
    'Entity.PipelinePlannedPlantationHeader.VendorName',
    VendorService
  )
    .required()
    .display('fantasyName')
    .displayFromNested('vendorName')
    .sortBy('vendorName')
    .keyField('id')
    .loadWith((service, query, config) => loadPipelinePlannedPlantationHeaderVendors(service, query, config))
    .prefilter({ multiple: true, order: 1 })
    .build(),
];

export const PIPELINEPLANNEDPLANTATIONHEADER_CONFIG = {
  entityName: 'PipelinePlannedPlantationHeader',
  localizationKey: 'Operational::Entity.PipelinePlannedPlantationHeader',
  fields: PIPELINEPLANNEDPLANTATIONHEADER_FIELDS,
  permission: 'PlanningOperation.PipelinePlannedPlantationHeaders',
  actions: PIPELINEPLANNEDPLANTATIONHEADER_ACTIONS_CONFIG,
  actionIds: PIPELINEPLANNEDPLANTATIONHEADER_ACTION_IDS,
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.PlanningOperational + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.Silviculture + MENU_NAME_SUFFIX,
  children: [
    {
        entityName: 'PipelinePlannedPlantation',
        foreignKeyField: 'pipelinePlannedPlantationHeaderId',
        localizationKey: 'Operational::Entity.PipelinePlannedPlantation',
        component: PipelinePlannedPlantation,
    }
  ]
};
